import React, { useState } from 'react';
import { Button, Input } from '../ui';
import { 
  Printer, 
  Award, 
  Sparkles, 
  ShieldCheck, 
  RefreshCw, 
  Sliders, 
  Check 
} from 'lucide-react';
import { cn } from '../../lib/utils';

export type CertificateTheme = 'prestige' | 'bauhaus' | 'modern' | 'parchment';
export type SealStyle = 'gold' | 'blue' | 'crest' | 'none';

interface MembershipPreset {
  name: string;
  tier: string;
  statement: string;
  theme: CertificateTheme;
  seal: SealStyle;
}

const PRESETS: MembershipPreset[] = [
  {
    name: 'Founding Member',
    tier: 'Founding Member',
    statement: 'In recognition of foundational leadership, steadfast loyalty, and distinguished commitment to the charter and enduring future of the organization.',
    theme: 'prestige',
    seal: 'gold'
  },
  {
    name: 'Lifetime Fellow',
    tier: 'Lifetime Fellow',
    statement: 'Conferred in grateful acknowledgment of meritorious service, exceptional dedication, and lasting contributions to our community and profession.',
    theme: 'parchment',
    seal: 'crest'
  },
  {
    name: 'Board of Directors',
    tier: 'Board Member',
    statement: 'Duly appointed and certified as a member in good standing of the Governing Board, entrusted with fiduciary governance and executive stewardship.',
    theme: 'bauhaus',
    seal: 'blue'
  },
  {
    name: 'Active Member',
    tier: 'Active Member in Good Standing',
    statement: 'Having fulfilled all bylaw requirements, ethical standards, and dues pledges, is hereby recognized as a certified voting member of the organization.',
    theme: 'modern',
    seal: 'gold'
  }
];

export function MembershipCertificateMaker() {
  const [recipientName, setRecipientName] = useState('Alexander J. Vance');
  const [orgName, setOrgName] = useState('National Council of Civic Leaders');
  const [tier, setTier] = useState('Founding Member');
  const [statement, setStatement] = useState(
    'In recognition of foundational leadership, steadfast loyalty, and distinguished commitment to the charter and enduring future of the organization.'
  );
  const [certificateId, setCertificateId] = useState('ORG-2026-0842');
  const [issueDate, setIssueDate] = useState('October 1, 2026');
  const [validUntil, setValidUntil] = useState('Lifetime / In Perpetuity');
  const [signer1Title, setSigner1Title] = useState('President & Board Chair');
  const [signer1Name, setSigner1Name] = useState('Dr. Marcus Sterling');
  const [signer2Title, setSigner2Title] = useState('Secretary of the Board');
  const [signer2Name, setSigner2Name] = useState('Elena Rostova');
  const [theme, setTheme] = useState<CertificateTheme>('prestige');
  const [sealStyle, setSealStyle] = useState<SealStyle>('gold');
  const [zoomLevel, setZoomLevel] = useState<'fit' | '100%'>('fit');

  const generateId = () => {
    const randomHex = Math.floor(1000 + Math.random() * 9000);
    setCertificateId(`ORG-${new Date().getFullYear()}-${randomHex}`);
  };

  const applyPreset = (preset: MembershipPreset) => {
    setTier(preset.tier);
    setStatement(preset.statement);
    setTheme(preset.theme);
    setSealStyle(preset.seal);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="w-full text-left">
      {/* SCREEN CONTROLS - Hidden during print */}
      <div className="print:hidden space-y-8">
        
        {/* Top Control Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-[3px] border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A] shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border-[2px] border-black dark:border-white bg-[#FFD600] text-black flex items-center justify-center font-black">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-black font-display uppercase tracking-tight">
                Official Membership Certificate Maker
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                Generate high-resolution, printable membership certificates & induction awards
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              className="bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase text-xs tracking-wider px-5 py-3 border-[2px] border-black dark:border-white shadow-[3px_3px_0px_#000] flex items-center gap-2 cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF
            </Button>
          </div>
        </div>

        {/* Preset Selector */}
        <div className="border-[3px] border-black dark:border-white p-4 bg-white dark:bg-black shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono font-black uppercase tracking-widest text-slate-500">
              Quick Recognition Presets:
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => applyPreset(p)}
                className={`p-2.5 text-left border-[2px] border-black dark:border-white font-bold text-xs transition-all cursor-pointer ${
                  tier === p.tier
                    ? 'bg-[#FFD600] text-black shadow-[2px_2px_0px_#000]'
                    : 'bg-[#F5F5F0] dark:bg-slate-900 text-black dark:text-white hover:bg-slate-200 dark:hover:bg-slate-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-black uppercase tracking-tight">{p.name}</span>
                  {tier === p.tier && <Check className="w-3.5 h-3.5" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* 2-Column Editor + Live Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Controls Column */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border-[3px] border-black dark:border-white p-6 bg-white dark:bg-black shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#fff] space-y-5">
              <h3 className="text-sm font-black font-mono uppercase tracking-widest border-b-[2px] border-black dark:border-white pb-2 flex items-center gap-2">
                <Sliders className="w-4 h-4 text-[#0044FF]" />
                Certificate Data
              </h3>

              {/* Recipient Name */}
              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Recipient Full Name:
                </label>
                <Input
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="e.g. Johnathan Doe"
                  className="font-bold border-[2px] border-black dark:border-white"
                />
              </div>

              {/* Organization Name */}
              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Organization / Chapter Name:
                </label>
                <Input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. Sierra Hiking Club"
                  className="font-bold border-[2px] border-black dark:border-white"
                />
              </div>

              {/* Membership Tier */}
              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Membership Designation / Title:
                </label>
                <Input
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  placeholder="e.g. Honorary Lifetime Member"
                  className="font-bold border-[2px] border-black dark:border-white"
                />
              </div>

              {/* Citation Body Text */}
              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Recognition Statement / Citation:
                </label>
                <textarea
                  rows={3}
                  value={statement}
                  onChange={(e) => setStatement(e.target.value)}
                  className="w-full p-2.5 text-xs font-serif border-[2px] border-black dark:border-white bg-transparent text-black dark:text-white focus:outline-none"
                />
              </div>

              {/* Certificate ID & Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] font-mono font-black uppercase tracking-widest">
                      Cert / Member ID:
                    </label>
                    <button
                      type="button"
                      onClick={generateId}
                      className="text-[9px] font-mono font-bold text-[#0044FF] hover:underline flex items-center gap-0.5"
                    >
                      <RefreshCw className="w-2.5 h-2.5" /> New
                    </button>
                  </div>
                  <Input
                    value={certificateId}
                    onChange={(e) => setCertificateId(e.target.value)}
                    className="font-mono text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                    Issue Date:
                  </label>
                  <Input
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    className="text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
              </div>

              {/* Validity */}
              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Validity / Expiration:
                </label>
                <Input
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className="text-xs border-[2px] border-black dark:border-white"
                />
              </div>

              {/* Signers */}
              <div className="pt-2 border-t-[2px] border-black dark:border-white">
                <span className="text-[10px] font-mono font-black uppercase tracking-widest block mb-2 text-slate-500">
                  Authorized Signatures
                </span>
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <div>
                    <label className="text-[9px] font-mono uppercase block mb-1">Signer 1 Name:</label>
                    <Input
                      value={signer1Name}
                      onChange={(e) => setSigner1Name(e.target.value)}
                      className="text-xs border-[2px] border-black dark:border-white"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono uppercase block mb-1">Signer 1 Title:</label>
                    <Input
                      value={signer1Title}
                      onChange={(e) => setSigner1Title(e.target.value)}
                      className="text-xs border-[2px] border-black dark:border-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[9px] font-mono uppercase block mb-1">Signer 2 Name:</label>
                    <Input
                      value={signer2Name}
                      onChange={(e) => setSigner2Name(e.target.value)}
                      className="text-xs border-[2px] border-black dark:border-white"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono uppercase block mb-1">Signer 2 Title:</label>
                    <Input
                      value={signer2Title}
                      onChange={(e) => setSigner2Title(e.target.value)}
                      className="text-xs border-[2px] border-black dark:border-white"
                    />
                  </div>
                </div>
              </div>

              {/* Style & Theme Selector */}
              <div className="pt-2 border-t-[2px] border-black dark:border-white space-y-3">
                <span className="text-[10px] font-mono font-black uppercase tracking-widest block text-slate-500">
                  Visual Theme & Seal
                </span>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'prestige', label: 'Classic Prestige' },
                    { id: 'bauhaus', label: 'Bauhaus Modern' },
                    { id: 'modern', label: 'Minimalist Slate' },
                    { id: 'parchment', label: 'Gold Parchment' },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTheme(t.id as CertificateTheme)}
                      className={`p-2 text-xs font-bold border-[2px] border-black dark:border-white uppercase tracking-wider ${
                        theme === t.id
                          ? 'bg-[#FFD600] text-black shadow-[2px_2px_0px_#000]'
                          : 'bg-white dark:bg-black text-black dark:text-white'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div>
                  <label className="text-[9px] font-mono uppercase block mb-1">Official Seal:</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { id: 'gold', label: 'Gold' },
                      { id: 'blue', label: 'Blue' },
                      { id: 'crest', label: 'Crest' },
                      { id: 'none', label: 'None' },
                    ].map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSealStyle(s.id as SealStyle)}
                        className={`p-1.5 text-[10px] font-mono font-bold border-[2px] border-black dark:border-white uppercase ${
                          sealStyle === s.id
                            ? 'bg-[#0044FF] text-white'
                            : 'bg-white dark:bg-slate-900 text-black dark:text-white'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t-[2px] border-black dark:border-white">
                <Button
                  onClick={handlePrint}
                  className="w-full bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase tracking-widest py-3 border-[2px] border-black dark:border-white shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Printer className="w-4 h-4" />
                  Print / Export to PDF
                </Button>
                <p className="text-[10px] font-mono text-center text-slate-500 mt-2">
                  Tip: In the print dialog, select Landscape layout with Margins set to "None".
                </p>
              </div>

            </div>
          </div>

          {/* Live Preview Column */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono font-black uppercase tracking-widest text-slate-500">
                Live Certificate Preview (Landscape 11" x 8.5"):
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setZoomLevel(zoomLevel === 'fit' ? '100%' : 'fit')}
                  className="border-[2px] border-black dark:border-white px-2 py-1 text-[10px] font-mono font-bold bg-white dark:bg-slate-900 text-black dark:text-white uppercase"
                >
                  {zoomLevel === 'fit' ? 'Zoom 100%' : 'Fit Screen'}
                </button>
              </div>
            </div>

            <div className="border-[4px] border-black dark:border-white bg-slate-200 dark:bg-slate-950 p-4 sm:p-8 overflow-x-auto flex justify-center shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
              <div 
                className={cn(
                  "transition-transform origin-top",
                  zoomLevel === 'fit' ? "scale-[0.45] sm:scale-[0.6] md:scale-[0.7] xl:scale-[0.8] 2xl:scale-[0.85]" : "scale-100"
                )}
                style={{ width: '920px', height: '650px' }}
              >
                <CertificateCanvas
                  recipientName={recipientName}
                  orgName={orgName}
                  tier={tier}
                  statement={statement}
                  certificateId={certificateId}
                  issueDate={issueDate}
                  validUntil={validUntil}
                  signer1Title={signer1Title}
                  signer1Name={signer1Name}
                  signer2Title={signer2Title}
                  signer2Name={signer2Name}
                  theme={theme}
                  sealStyle={sealStyle}
                />
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* PRINT-ONLY VIEW */}
      <div className="hidden print:block fixed inset-0 m-0 p-0 bg-white z-[99999]">
        <style type="text/css" media="print">
          {`
            @page {
              size: landscape;
              margin: 0;
            }
            body {
              margin: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              background-color: white !important;
            }
          `}
        </style>
        <div className="w-screen h-screen flex items-center justify-center p-6 bg-white">
          <div style={{ width: '1020px', height: '720px' }}>
            <CertificateCanvas
              recipientName={recipientName}
              orgName={orgName}
              tier={tier}
              statement={statement}
              certificateId={certificateId}
              issueDate={issueDate}
              validUntil={validUntil}
              signer1Title={signer1Title}
              signer1Name={signer1Name}
              signer2Title={signer2Title}
              signer2Name={signer2Name}
              theme={theme}
              sealStyle={sealStyle}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Certificate Visual Component ─────────────────────────────────────────────

interface CertificateCanvasProps {
  recipientName: string;
  orgName: string;
  tier: string;
  statement: string;
  certificateId: string;
  issueDate: string;
  validUntil: string;
  signer1Title: string;
  signer1Name: string;
  signer2Title: string;
  signer2Name: string;
  theme: CertificateTheme;
  sealStyle: SealStyle;
}

function CertificateCanvas({
  recipientName,
  orgName,
  tier,
  statement,
  certificateId,
  issueDate,
  validUntil,
  signer1Title,
  signer1Name,
  signer2Title,
  signer2Name,
  theme,
  sealStyle
}: CertificateCanvasProps) {
  // Theme-specific styling
  const isBauhaus = theme === 'bauhaus';
  const isParchment = theme === 'parchment';
  const isPrestige = theme === 'prestige';
  const isModern = theme === 'modern';

  return (
    <div
      className={cn(
        "w-full h-full relative select-none flex flex-col justify-between p-10 box-border text-black shadow-2xl",
        isPrestige && "bg-[#FAF9F5] border-[16px] border-[#0A192F] shadow-none",
        isBauhaus && "bg-white border-[12px] border-black",
        isParchment && "bg-[#FFFDF2] border-[14px] border-[#593E1A]",
        isModern && "bg-white border-[10px] border-slate-900"
      )}
    >
      {/* Decorative Inner Border */}
      {isPrestige && (
        <div className="absolute inset-3 border-[2px] border-[#C5A059] pointer-events-none flex flex-col justify-between p-1">
          <div className="w-full h-full border border-[#C5A059]/40 relative">
            <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-[#C5A059]" />
            <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-[#C5A059]" />
            <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-[#C5A059]" />
            <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-[#C5A059]" />
          </div>
        </div>
      )}

      {isParchment && (
        <div className="absolute inset-3 border-[3px] border-[#B8860B] pointer-events-none p-1.5">
          <div className="w-full h-full border border-[#8B4513]/40" />
        </div>
      )}

      {isBauhaus && (
        <>
          <div className="absolute top-0 right-0 w-24 h-24 bg-[#0044FF] border-b-[6px] border-l-[6px] border-black pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-[#FFD600] border-t-[6px] border-r-[6px] border-black pointer-events-none" />
        </>
      )}

      {/* Top Header Section */}
      <div className="relative z-10 text-center space-y-2 mt-2">
        <p 
          className={cn(
            "text-xs uppercase tracking-[0.3em] font-bold",
            isBauhaus ? "font-mono text-black font-black" : "text-[#718096] font-sans"
          )}
        >
          {orgName || 'ORGANIZATION NAME'}
        </p>

        <h1 
          className={cn(
            "leading-none",
            isPrestige && "font-serif text-4xl sm:text-5xl font-black text-[#0A192F] tracking-wide",
            isBauhaus && "font-black font-display text-4xl sm:text-5xl uppercase tracking-tighter text-black",
            isParchment && "font-serif text-4xl sm:text-5xl font-bold text-[#3D260F] tracking-wider",
            isModern && "font-sans text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-900"
          )}
        >
          Certificate of Membership
        </h1>

        <p className="text-xs uppercase tracking-[0.25em] text-[#A0AEC0] font-mono">
          THIS IS PROUDLY PRESENTED TO
        </p>
      </div>

      {/* Recipient Section */}
      <div className="relative z-10 text-center my-auto py-2 space-y-3">
        <div className="inline-block relative">
          <h2 
            className={cn(
              "text-3xl sm:text-4xl px-8 pb-2 leading-none",
              isPrestige && "font-serif italic text-[#1A202C] border-b-2 border-[#C5A059]",
              isBauhaus && "font-black font-display uppercase tracking-tight text-black border-b-[4px] border-black",
              isParchment && "font-serif font-bold text-[#2A1810] border-b-2 border-[#8B4513]",
              isModern && "font-sans font-black text-slate-900 border-b-[3px] border-slate-900"
            )}
          >
            {recipientName || 'Member Name'}
          </h2>
        </div>

        <div className="space-y-1">
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
            AS A DULY RECOGNIZED & CERTIFIED
          </p>
          <div className="inline-block">
            <span 
              className={cn(
                "px-4 py-1 text-sm font-black uppercase tracking-wider",
                isPrestige && "bg-[#0A192F] text-[#C5A059] font-serif",
                isBauhaus && "bg-black text-[#FFD600] font-display border-[2px] border-black shadow-[2px_2px_0px_#000]",
                isParchment && "bg-[#593E1A] text-[#FFE8AA] font-serif",
                isModern && "bg-slate-900 text-white font-sans"
              )}
            >
              {tier || 'Active Member'}
            </span>
          </div>
        </div>

        <p 
          className={cn(
            "max-w-xl mx-auto text-xs leading-relaxed mt-2 px-6",
            isPrestige && "font-serif italic text-slate-700",
            isBauhaus && "font-sans font-bold text-slate-800 text-[11px]",
            isParchment && "font-serif text-slate-800 text-[11px]",
            isModern && "font-sans text-slate-600 text-[11px]"
          )}
        >
          {statement}
        </p>
      </div>

      {/* Bottom Signatures & Seal Section */}
      <div className="relative z-10 pt-4 flex items-end justify-between px-6 mb-2">
        
        {/* Signer 1 */}
        <div className="w-48 text-center">
          <div className="h-9 flex items-end justify-center pb-1">
            <span className="font-serif italic text-base text-slate-800 select-none">
              {signer1Name}
            </span>
          </div>
          <div 
            className={cn(
              "w-full border-t pt-1.5",
              isPrestige ? "border-[#C5A059]" : "border-black"
            )}
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-900 leading-tight">
              {signer1Name || 'Authorized Signer'}
            </p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
              {signer1Title || 'President'}
            </p>
          </div>
        </div>

        {/* Center Seal */}
        <div className="flex flex-col items-center justify-center">
          {sealStyle === 'gold' && (
            <div className="w-20 h-20 rounded-full border-[3px] border-[#C5A059] bg-[#FFEFA6] p-1 flex items-center justify-center shadow-md relative">
              <div className="w-full h-full rounded-full border border-dashed border-[#8A6D3B] flex flex-col items-center justify-center text-center">
                <Sparkles className="w-5 h-5 text-[#8A6D3B] mb-0.5" />
                <span className="text-[7px] font-black uppercase tracking-widest text-[#5C4033] leading-none">
                  OFFICIAL
                </span>
                <span className="text-[6px] font-mono font-bold text-[#5C4033]">SEAL</span>
              </div>
            </div>
          )}

          {sealStyle === 'blue' && (
            <div className="w-20 h-20 rounded-full border-[3px] border-black bg-[#0044FF] p-1 flex items-center justify-center shadow-[3px_3px_0px_#000]">
              <div className="w-full h-full rounded-full border border-white flex flex-col items-center justify-center text-center text-white">
                <Award className="w-5 h-5 mb-0.5" />
                <span className="text-[7px] font-black uppercase tracking-widest leading-none">VERIFIED</span>
                <span className="text-[6px] font-mono">CHARTER</span>
              </div>
            </div>
          )}

          {sealStyle === 'crest' && (
            <div className="w-20 h-20 rounded-full border-[3px] border-[#8B4513] bg-[#EED9B3] p-1 flex items-center justify-center">
              <div className="w-full h-full rounded-full border border-[#593E1A] flex flex-col items-center justify-center text-center text-[#593E1A]">
                <ShieldCheck className="w-5 h-5 mb-0.5" />
                <span className="text-[7px] font-black uppercase tracking-widest leading-none">HONOR</span>
                <span className="text-[6px] font-serif font-bold">COUNCIL</span>
              </div>
            </div>
          )}

          {sealStyle === 'none' && (
            <div className="h-16 flex items-center justify-center text-[10px] font-mono text-slate-400">
              [ OFFICIAL SEAL ]
            </div>
          )}

          <div className="mt-1 text-center font-mono text-[8px] text-slate-500 uppercase tracking-widest">
            ID: {certificateId || 'ORG-0000'}
          </div>
        </div>

        {/* Signer 2 */}
        <div className="w-48 text-center">
          <div className="h-9 flex items-end justify-center pb-1">
            <span className="font-serif italic text-base text-slate-800 select-none">
              {signer2Name}
            </span>
          </div>
          <div 
            className={cn(
              "w-full border-t pt-1.5",
              isPrestige ? "border-[#C5A059]" : "border-black"
            )}
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-900 leading-tight">
              {signer2Name || 'Authorized Signer'}
            </p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
              {signer2Title || 'Secretary'}
            </p>
          </div>
        </div>

      </div>

      {/* Micro Metadata Footer Bar */}
      <div className="relative z-10 flex justify-between items-center text-[8px] font-mono text-slate-400 border-t border-slate-200 dark:border-slate-800/40 pt-1.5 px-4">
        <span>ISSUED: {issueDate}</span>
        <span>VALIDITY: {validUntil}</span>
        <span>SECURITY VERIFICATION: {certificateId}</span>
      </div>

    </div>
  );
}
